// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import useRequestRegistrarService from '../../../hooks/mutations/registry/onboarding/useRequestRegistrarService';
import useMutate from '../../../hooks/mutations/useMutate';
import {
  ERR_CREATE_REQUEST_REGISTRAR_SERVICE,
  SUC_CREATE_REQUEST_REGISTRAR_SERVICE,
} from '../../../utils/strings';

type DialogProps = {
  open: boolean;
  onClose: () => void;
};

export const RequestRegistrarServiceDialog = ({ open, onClose }: DialogProps) => {
  const [isLoading, setLoading] = useState(false);
  const [upgrader, setUpgrader] = useState<string>('');
  const requestRegistrarService = useRequestRegistrarService();
  const mutate = useMutate();

  const handleClose = useCallback(() => {
    setLoading(false);
    setUpgrader('');
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    try {
      await mutate(
        requestRegistrarService,
        { upgrader },
        SUC_CREATE_REQUEST_REGISTRAR_SERVICE,
        ERR_CREATE_REQUEST_REGISTRAR_SERVICE,
      );
    } finally {
      handleClose();
      setLoading(false);
    }
  }, [handleClose, mutate, requestRegistrarService, upgrader]);

  const disableSubmit = !upgrader || isLoading;

  return (
    <FormDialog
      open={open}
      title="Request Registrar Service"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Request"
      isSubmitDisabled={disableSubmit}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextInput
          enableTopLabel
          label="Upgrader Party ID"
          value={upgrader}
          setValue={setUpgrader}
        />
      </Box>
    </FormDialog>
  );
};
